import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { Alert, errorText, Field, Loading, useLoad } from '../components/ui';
import { dateOnly, won } from '../format';
import { GuardianTasks } from './Tasks';

/**
 * 학생 화면: 자녀 모드(보호자 기기, 만 19세 미만) 또는 만 14세 이상 학생 계정.
 * 센터 이용자와 연결돼 있으면 선생님이 낸 과제를 하고, 결과물 사진을 올린다.
 */
export function Student() {
  const q = useLoad(() => api.studentHome(), []);
  const h = q.data;
  return (
    <div className="student">
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !h && <Loading />}
      {h && <>
        <div className="student-hello">
          <span className="student-wave" aria-hidden="true">👋</span>
          <div>
            <h1>{h.name}, 안녕!</h1>
            <p className="muted">{h.link ? `${h.link.centerName} 선생님이 낸 오늘의 과제예요.` : '오늘도 즐겁게 해 봐요.'}</p>
          </div>
        </div>
        {h.link
          ? <GuardianTasks clientId={h.link.clientId} name={h.name} />
          : <div className="empty">
              <p><strong>아직 센터와 연결되지 않았어요.</strong></p>
              <p className="muted small">{h.childMode ? '보호자가 우리 아이 화면에서 센터 연결 코드를 넣으면 과제가 여기에 나타나요.' : '센터와 연결되면 과제가 여기에 나타나요.'}</p>
            </div>}
        {!h.childMode && <PayerCard />}
        {h.childMode && <p className="hint student-exit">자녀 모드 · 보호자 화면은 로그아웃 후 다시 로그인</p>}
      </>}
    </div>
  );
}

/** 직접 가입한 학생: 결제를 맡아 줄 보호자를 코드로 잇는다. */
function PayerCard() {
  const q = useLoad(() => api.payerGuardian(), []);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); setCode(''); q.reload(); } catch (e) { setError(errorText(e)); }
    setBusy(false);
  };
  const guardian = q.data?.guardian;
  if (q.loading && !q.data) return null;
  return (
    <section className="card stack" aria-labelledby="payer-title">
      <h2 id="payer-title">결제 보호자</h2>
      {error && <Alert>{error}</Alert>}
      {guardian ? (
        <>
          <p>{guardian.guardianName} 보호자님과 연결되어 있습니다. 유료 이용이 필요하면 보호자님이 직접 결제합니다. 카드 정보는 이 화면에 보이지 않습니다.</p>
          <p>{q.data?.passUntil ? `학생 월 이용권: ${dateOnly(q.data.passUntil)}까지` : '학생 월 이용권이 없습니다.'}</p>
          {guardian.passRequestedAt
            ? <p className="muted small">보호자님께 결제를 요청했습니다.</p>
            : <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run(() => api.requestPass())}>보호자에게 월 이용권 결제 요청 ({won(q.data?.price ?? 0)})</button>}
          <button type="button" className="btn btn-outline" disabled={busy} onClick={() => void run(() => api.endPayerLink())}>연결 해제</button>
        </>
      ) : (
        <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); void run(() => api.connectPayer(code)); }}>
          <p className="muted small">보호자가 우리 아이 화면에서 만든 코드를 넣으면 보호자님이 결제를 맡을 수 있습니다.</p>
          <Field id="payer-code" label="보호자 연결 코드">
            <input id="payer-code" required autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={12} className="mono" value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <button type="submit" className="btn btn-primary" disabled={busy}>연결</button>
        </form>
      )}
    </section>
  );
}
